import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import type { Invitation } from "./InvitationModal";

export type Rsvp = {
  id: string;
  invitation_id: string;
  tenant_id: string;
  guest_name: string;
  phone: string | null;
  email: string | null;
  attendance_status: string;
  guest_count: number;
  message: string | null;
  created_at: string;
};

const STATUS_STYLES: Record<string, string> = {
  yes:   "bg-emerald-50 text-emerald-600",
  no:    "bg-red-50 text-red-500",
  maybe: "bg-amber-50 text-amber-500",
};
const STATUS_LABELS: Record<string, string> = {
  yes:   "Attending",
  no:    "Not Attending",
  maybe: "Maybe",
};
const STATUS_ICONS: Record<string, string> = {
  yes:   "ri-checkbox-circle-fill",
  no:    "ri-close-circle-line",
  maybe: "ri-question-line",
};

function exportCSV(rsvps: Rsvp[], invTitle: string) {
  const headers = ["Name", "Phone", "Email", "Attendance", "Guests", "Message", "Date"];
  const rows = rsvps.map((r) => [
    r.guest_name,
    r.phone ?? "",
    r.email ?? "",
    STATUS_LABELS[r.attendance_status] ?? r.attendance_status,
    String(r.guest_count),
    (r.message ?? "").replace(/,/g, ";"),
    new Date(r.created_at).toLocaleDateString("en-US"),
  ]);
  const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `rsvp-${invTitle.replace(/\s+/g, "-").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

interface Props {
  invitation: Invitation;
  tenantId: string;
  onClose: () => void;
}

export default function RsvpModal({ invitation, tenantId, onClose }: Props) {
  const [rsvps, setRsvps]     = useState<Rsvp[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState<"all" | "yes" | "no" | "maybe">("all");
  const [busyId, setBusyId]   = useState<string | null>(null);

  const fetchRsvps = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("invitation_rsvps")
      .select("*")
      .eq("invitation_id", invitation.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else {
      setRsvps((data as Rsvp[]) ?? []);
    }
    setLoading(false);
  }, [invitation.id, tenantId]);

  useEffect(() => { fetchRsvps(); }, [fetchRsvps]);

  const handleDelete = async (rsvp: Rsvp) => {
    const result = await Swal.fire({
      title: "Remove RSVP?",
      text: `Remove "${rsvp.guest_name}"'s response?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;
    setBusyId(rsvp.id);
    const { error } = await supabase.from("invitation_rsvps").delete().eq("id", rsvp.id).eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else {
      setRsvps((prev) => prev.filter((r) => r.id !== rsvp.id));
    }
    setBusyId(null);
  };

  const yesCount    = rsvps.filter((r) => r.attendance_status === "yes").length;
  const noCount     = rsvps.filter((r) => r.attendance_status === "no").length;
  const maybeCount  = rsvps.filter((r) => r.attendance_status === "maybe").length;
  const totalGuests = rsvps.filter((r) => r.attendance_status === "yes").reduce((sum, r) => sum + r.guest_count, 0);
  const filtered    = filter === "all" ? rsvps : rsvps.filter((r) => r.attendance_status === filter);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-4 h-4 flex items-center justify-center text-[#C8A96E]">
                <i className="ri-group-line text-sm"></i>
              </span>
              <p className="text-slate-400 text-xs uppercase tracking-wider">RSVPs</p>
            </div>
            <h2 className="text-[#0f172a] font-semibold text-base truncate">{invitation.title}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {rsvps.length > 0 && (
              <button
                onClick={() => exportCSV(rsvps, invitation.title)}
                className="flex items-center gap-1.5 border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-download-2-line"></i>
                </span>
                Export CSV
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: "Attending",   value: yesCount,     color: "text-emerald-600", bg: "bg-emerald-50", sub: `${totalGuests} total guests` },
              { label: "Not Coming",  value: noCount,      color: "text-red-500",     bg: "bg-red-50",     sub: "" },
              { label: "Maybe",       value: maybeCount,   color: "text-amber-500",   bg: "bg-amber-50",   sub: "" },
              { label: "Total RSVPs", value: rsvps.length, color: "text-[#0f172a]",   bg: "bg-slate-50",   sub: "" },
            ].map(({ label, value, color, bg, sub }) => (
              <div key={label} className={`${bg} rounded-xl p-3 border border-slate-100 text-center`}>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-slate-400 text-xs mt-0.5">{label}</p>
                {sub && <p className="text-slate-300 text-[10px] mt-0.5">{sub}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="px-6 py-3 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1 w-fit">
            {(["all", "yes", "no", "maybe"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  filter === f ? "bg-[#C8A96E] text-white" : "text-slate-400 hover:text-slate-700"
                }`}
              >
                {f === "all" ? "All" : STATUS_LABELS[f]}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 text-slate-300">
                <i className="ri-group-line text-2xl"></i>
              </div>
              <p className="text-slate-400 text-sm">
                {rsvps.length === 0 ? "No RSVPs yet" : "No RSVPs in this filter"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((rsvp) => {
                const isBusy = busyId === rsvp.id;
                return (
                  <div key={rsvp.id} className="py-3.5 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#C8A96E]/10 text-[#C8A96E] flex items-center justify-center text-sm font-semibold shrink-0">
                      {rsvp.guest_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-[#0f172a] text-sm font-medium">{rsvp.guest_name}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${STATUS_STYLES[rsvp.attendance_status] ?? "bg-slate-100 text-slate-400"}`}>
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className={STATUS_ICONS[rsvp.attendance_status] ?? "ri-question-line"}></i>
                          </span>
                          {STATUS_LABELS[rsvp.attendance_status] ?? rsvp.attendance_status}
                        </span>
                        {rsvp.attendance_status === "yes" && rsvp.guest_count > 1 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">
                            +{rsvp.guest_count - 1} guests
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                        {rsvp.phone && <span className="flex items-center gap-1"><i className="ri-phone-line text-[10px]"></i>{rsvp.phone}</span>}
                        {rsvp.email && <span className="flex items-center gap-1"><i className="ri-mail-line text-[10px]"></i>{rsvp.email}</span>}
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line text-[10px]"></i>
                          {new Date(rsvp.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </div>
                      {rsvp.message && (
                        <p className="text-slate-400 text-xs mt-1.5 italic line-clamp-2">
                          &ldquo;{rsvp.message}&rdquo;
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(rsvp)}
                      disabled={isBusy}
                      className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-all cursor-pointer shrink-0 disabled:opacity-40"
                    >
                      {isBusy
                        ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
                        : <i className="ri-delete-bin-line text-sm"></i>
                      }
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 shrink-0 flex justify-end">
          <button onClick={onClose} className="px-5 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}